// 진열 순서 변경용 드래그 리스트(관리자 진열관리/카테고리 순서 모달 공용).
// 데스크톱 관리자 화면 전제라 HTML5 네이티브 드래그앤드롭을 쓰고, 접근성/보조 수단으로 위/아래 버튼도 둔다.
// 컨트롤드 컴포넌트: 현재 순서(items)를 받아 그려주고, 순서가 바뀌면 onReorder(next)로 새 배열을 올려보낸다.

import { useRef, useState } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import type { ProductDto } from "@oo/shared";
import { imageUrl } from "@/features/catalog/imageUrl.ts";

interface Props {
  items: ProductDto[];
  onReorder: (next: ProductDto[]) => void;
}

// 배열에서 from 위치 항목을 to 위치로 옮긴 새 배열을 만든다.
function move<T>(arr: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) {
    return arr;
  }
  const next = [...arr];
  const [m] = next.splice(from, 1);
  if (!m) return arr;
  next.splice(to, 0, m);
  return next;
}

// 상품 진열 순서 드래그 리스트. 현재 순서를 그리고, 드래그/버튼으로 바뀐 새 순서를 onReorder 로 알린다.
export default function ProductReorderList({ items, onReorder }: Props) {
  // 드래그 중인 항목 인덱스(ref: 즉시값) + 드롭 대상 표시(state: 렌더 반영).
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-ink-300 p-6 text-center text-sm text-ink-600">
        표시할 상품이 없습니다.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {items.map((p, idx) => {
        const url = imageUrl(p.mainImageKey);
        const isOver = overIndex === idx;
        return (
          <li
            key={p.id}
            draggable
            onDragStart={() => {
              dragIndex.current = idx;
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (overIndex !== idx) setOverIndex(idx);
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndex.current;
              if (from !== null && from !== idx) {
                onReorder(move(items, from, idx));
              }
              dragIndex.current = null;
              setOverIndex(null);
            }}
            onDragEnd={() => {
              dragIndex.current = null;
              setOverIndex(null);
            }}
            className={`flex items-center gap-2 rounded-md border bg-white p-2 ${
              isOver ? "border-brand-600 bg-brand-50" : "border-ink-200"
            }`}
          >
            {/* 드래그 핸들 */}
            <span className="shrink-0 cursor-grab text-ink-400" aria-hidden="true">
              <GripVertical size={18} />
            </span>
            {/* 순번 */}
            <span className="w-6 shrink-0 text-center text-xs font-semibold tabular-nums text-ink-500">
              {idx + 1}
            </span>
            {/* 이미지 */}
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded border border-ink-200 bg-ink-50">
              {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            {/* 이름 + 상태 */}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{p.name}</p>
              <p className="flex flex-wrap gap-1 text-[10px] text-ink-600">
                <span>{p.priceKrw.toLocaleString()}원</span>
                {!p.isVisible ? <span className="text-ink-500">· 숨김</span> : null}
                {p.isSoldOut ? <span className="text-danger-600">· 품절</span> : null}
                {p.isBest ? <span className="text-warning-700">· 베스트</span> : null}
              </p>
            </div>
            {/* 위/아래 이동(보조 수단) */}
            <div className="flex shrink-0 flex-col">
              <button
                type="button"
                aria-label="위로"
                disabled={idx === 0}
                onClick={() => onReorder(move(items, idx, idx - 1))}
                className="rounded p-0.5 text-ink-600 hover:bg-ink-100 disabled:opacity-30"
              >
                <ChevronUp size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label="아래로"
                disabled={idx === items.length - 1}
                onClick={() => onReorder(move(items, idx, idx + 1))}
                className="rounded p-0.5 text-ink-600 hover:bg-ink-100 disabled:opacity-30"
              >
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
